import { useState, useEffect, useCallback } from 'react';
import { rewardService } from '@/services/rewardService';
import { AI_POINTS, getAIChatPoints } from '@/config/pointsConfig';

/**
 * Hook quản lý điểm thưởng trong ứng dụng
 * Cung cấp các tiện ích để lấy, kiểm tra và trừ điểm
 */
export const usePoints = () => {
  const [points, setPoints] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Lấy số điểm hiện tại
  const fetchPoints = useCallback(async () => {
    try {
      setLoading(true);
      const currentPoints = await rewardService.getCurrentPoints();
      setPoints(currentPoints);
      setError(null);
    } catch (err) {
      setError('Không thể lấy thông tin điểm thưởng');
      console.error('Error fetching points:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Lấy điểm khi component mount
  useEffect(() => {
    fetchPoints();
  }, [fetchPoints]);

  // Kiểm tra xem có đủ điểm không
  const hasEnoughPoints = useCallback((requiredPoints: number): boolean => {
    return points !== null && points >= requiredPoints;
  }, [points]);

  // Lấy số điểm cần thiết cho một dịch vụ
  const getRequiredPoints = useCallback((serviceType: string, contentType?: string): number => {
    return rewardService.getRequiredPointsForService(serviceType, contentType);
  }, []);

  // Trừ điểm và cập nhật state
  const deductPoints = useCallback(async (
    points: number,
    serviceType: string,
    contentId?: string,
    description?: string
  ) => {
    try {
      setLoading(true);
      const result = await rewardService.deductPointsForAI(
        points,
        serviceType,
        contentId,
        description
      );
      
      if (result.success) {
        // Cập nhật lại số điểm sau khi trừ thành công
        await fetchPoints();
      }
      
      return result;
    } catch (err) {
      setError('Không thể trừ điểm');
      console.error('Error deducting points:', err);
      return { success: false, message: 'Đã xảy ra lỗi khi trừ điểm' };
    } finally {
      setLoading(false);
    }
  }, [fetchPoints]);

  // Kiểm tra và trừ điểm cho chat
  const checkAndDeductForChat = useCallback(async (
    contentType: string,
    contentId?: string,
    expertName?: string,
    contextName?: string
  ) => {
    const requiredPoints = getAIChatPoints(contentType);
    
    // Kiểm tra đủ điểm không
    if (!hasEnoughPoints(requiredPoints)) {
      return {
        success: false,
        message: `Không đủ điểm thưởng. Cần ít nhất ${requiredPoints} điểm.`,
        requiredPoints
      };
    }
    
    // Trừ điểm
    const result = await deductPoints(
      requiredPoints,
      'chat',
      contentId,
      `Chat với ${expertName || 'Thiền sư AI'} về ${contextName || 'chủ đề chung'}`
    );
    
    return {
      ...result,
      requiredPoints
    };
  }, [hasEnoughPoints, deductPoints]);

  return {
    points,
    loading,
    error,
    fetchPoints,
    hasEnoughPoints,
    getRequiredPoints,
    deductPoints,
    checkAndDeductForChat
  };
};

export default usePoints; 